export type AppPlatform = "ios" | "android" | "desktop";

export type DisplayMode = "browser" | "standalone" | "minimal-ui" | "fullscreen" | "unknown";

export type ClientContext = {
  platform: AppPlatform;
  displayMode: DisplayMode;
  isStandalone: boolean;
  /** True when iOS Safari/Chrome tab — NOT the Home Screen app. */
  isBrowserShell: boolean;
  /** Push is usable in this context (iOS requires Home Screen / standalone). */
  canUsePushHere: boolean;
  /** User must install / open from icon before signing up or enabling push. */
  mustInstallFirst: boolean;
  userAgent: string;
  language: string;
  languages: string[];
  timezone: string;
  vendor: string;
  platformRaw: string;
  maxTouchPoints: number;
  screenWidth: number;
  screenHeight: number;
  pixelRatio: number;
  deviceMemoryGb: number | null;
  hardwareConcurrency: number | null;
  connectionType: string | null;
};

export function isIos(): boolean {
  if (typeof navigator === "undefined") {
    return false;
  }
  const ua = navigator.userAgent;
  const iOS = /iPad|iPhone|iPod/.test(ua);
  const iPadOs = navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1;
  return iOS || iPadOs;
}

export function isAndroid(): boolean {
  return typeof navigator !== "undefined" && /Android/i.test(navigator.userAgent);
}

export function isStandalone(): boolean {
  if (typeof window === "undefined") {
    return false;
  }
  const media = window.matchMedia("(display-mode: standalone)").matches
    || window.matchMedia("(display-mode: fullscreen)").matches
    || window.matchMedia("(display-mode: minimal-ui)").matches;
  const iosStandalone =
    "standalone" in navigator
    && (navigator as Navigator & { standalone?: boolean }).standalone === true;
  return media || iosStandalone;
}

export function getDisplayMode(): DisplayMode {
  if (typeof window === "undefined") {
    return "unknown";
  }
  if (window.matchMedia("(display-mode: standalone)").matches) {
    return "standalone";
  }
  if (window.matchMedia("(display-mode: fullscreen)").matches) {
    return "fullscreen";
  }
  if (window.matchMedia("(display-mode: minimal-ui)").matches) {
    return "minimal-ui";
  }
  const iosStandalone =
    "standalone" in navigator
    && (navigator as Navigator & { standalone?: boolean }).standalone === true;
  if (iosStandalone) {
    return "standalone";
  }
  return "browser";
}

export function detectPlatform(): AppPlatform {
  if (isIos()) {
    return "ios";
  }
  if (isAndroid()) {
    return "android";
  }
  return "desktop";
}

export function getClientContext(): ClientContext {
  const platform = detectPlatform();
  const displayMode = getDisplayMode();
  const standalone = isStandalone();
  const isBrowserShell = !standalone;
  const mustInstallFirst = (platform === "ios" || platform === "android") && isBrowserShell;
  // iOS: push only works from Home Screen. Android: we still require install first so identity
  // (localStorage / service worker) lives in the same installed app context.
  const canUsePushHere = platform === "desktop" || standalone;

  const nav = navigator as Navigator & { deviceMemory?: number; connection?: { effectiveType?: string } };

  return {
    platform,
    displayMode,
    isStandalone: standalone,
    isBrowserShell,
    canUsePushHere,
    mustInstallFirst,
    userAgent: navigator.userAgent,
    language: navigator.language,
    languages: [...(navigator.languages ?? [navigator.language])],
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    vendor: navigator.vendor ?? "",
    platformRaw: navigator.platform ?? "",
    maxTouchPoints: navigator.maxTouchPoints ?? 0,
    screenWidth: window.screen?.width ?? 0,
    screenHeight: window.screen?.height ?? 0,
    pixelRatio: window.devicePixelRatio ?? 1,
    deviceMemoryGb: typeof nav.deviceMemory === "number" ? nav.deviceMemory : null,
    hardwareConcurrency: typeof navigator.hardwareConcurrency === "number" ? navigator.hardwareConcurrency : null,
    connectionType: nav.connection?.effectiveType ?? null
  };
}

/** Short human-readable label for this device (best-effort from UA). */
export function buildDeviceLabel(ctx: ClientContext = getClientContext()): string {
  const ua = ctx.userAgent;
  if (ctx.platform === "ios") {
    const version = ua.match(/OS (\d+[_\.]\d+)/)?.[1]?.replace("_", ".") ?? "";
    const device = /iPad/.test(ua) ? "iPad" : "iPhone";
    return version ? `${device} (iOS ${version})` : device;
  }
  if (ctx.platform === "android") {
    const model = ua.match(/Android [^;]*; ([^)]+)\)/)?.[1]?.trim();
    if (model && !/wv|Build/i.test(model)) {
      return `Android · ${model}`;
    }
    const ver = ua.match(/Android (\d+(?:\.\d+)?)/)?.[1];
    return ver ? `Android ${ver}` : "Android";
  }
  if (/Windows/i.test(ua)) {
    return "Windows";
  }
  if (/Mac OS X/i.test(ua)) {
    return "Mac";
  }
  return "Desktop";
}

export function collectDeviceInfo(): Record<string, unknown> {
  const ctx = getClientContext();
  return {
    ...ctx,
    deviceLabel: buildDeviceLabel(ctx),
    collectedAt: new Date().toISOString()
  };
}

export function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export function arrayBufferToBase64Url(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  bytes.forEach((b) => {
    binary += String.fromCharCode(b);
  });
  return window
    .btoa(binary)
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
}
